<script lang="ts" setup>
import type { FormModel as ModuleModel } from '#layers/studio/app/runtime/services/ModulesService';
import type ValidationSchema from '#layers/core/app/runtime/utils/validation';
import ModulesService from '#layers/studio/app/runtime/services/ModulesService';
import UsersService from '#layers/core/app/pages/system/admin/users/_runtime/services/UsersService';

defineOptions({
    name: 'ModuleDynamicFormsSettings'
});

const { $alert } = useAlerts();
const { $log } = useLogging();
const { $t } = useLocalization();

const model = defineModel<any>();
const defaults: any = {
    mode: 'module',
    dynamicFormOriginId: null,
    moduleId: null,
    sectionId: null,
    users: []
};
const modules = ref<Option<number>[]>([]);
const module = ref<ModuleModel>();
const userId = ref<number | null>(null);
const users = ref<any[]>([]);
const modulesTriggerKey = ref(0);
const sections = computed(() => ([
    { value: null, text: '' },
    ...(module.value?.sections.map(p => ({ value: p.id, text: p.name })) ?? [])
]));
const hasModuleSettingsErrors = computed(() =>
    form.value?.$errors?.has('settings.moduleId') || form.value?.$errors?.has('settings.sectionId')
);

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = { ...defaults, ...form.value.settings };
    form.value.settings.mode = 'module';

    await loadModules();
    await loadModule(form.value.settings.moduleId);
    getUsers();
});

watch(() => form.value?.settings, () =>
{
    model.value = form.value;
},
{ deep: true });

watch(() => form.value?.settings.moduleId, async (value, old) =>
{
    if (old)
    {
        form.value.settings.sectionId = null;
    }

    await loadModules();
    await loadModule(value);
});

async function loadModules()
{
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const key = modulesTriggerKey.value;

    try
    {
        const pager = new Pager(1, 10000, 'Name', 'ASC');
        const response = await ModulesService.getList({} as any, pager);

        modules.value = [{ value: null, text: '' }, ...response.items.map(p => ({
            value: p.id,
            text: p.name
        }))];
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });

        return false;
    }

    return true;
}

async function loadModule(moduleId: number)
{
    if (!moduleId) return;

    try
    {
        const response = await ModulesService.fetch(moduleId);

        module.value = response.result;
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
}

async function getUsers(): Promise<void>
{
    form.value.settings.users.forEach(async (u: number) =>
    {
        const response = await fetchUser(u);

        if (response)
        {
            users.value.push(response);
        }
    });
}

async function fetchUser(id: number): Promise<any>
{
    const response = await UsersService.fetchAutocompleteModel(id);

    return response || null;
}

async function searchUser(query: string, limit: number): Promise<any[]>
{
    return await UsersService.getAutocompleteList(query, limit);
}

async function addUser(): Promise<void>
{
    if (userId.value == null || form.value.settings.users.includes(userId.value)) return;

    form.value.settings.users.push(userId.value);

    const response = await fetchUser(userId.value);

    if (response)
    {
        users.value.push(response);
    }

    userId.value = null;
}

function deleteUser(id: number): void
{
    form.value.settings.users = form.value.settings.users.filter((u: number) => u !== id);

    users.value = users.value.filter((u: any) => u.id !== id);
}

async function onWizardDone(moduleId: number, sectionId: string)
{
    form.value.settings.moduleId = moduleId;
    form.value.settings.sectionId = sectionId;
    modulesTriggerKey.value++;
}

function validate(): void
{
    form.value.validate((schema: ValidationSchema) => ({
        settings: schema.object({
            moduleId: schema.number().nullable().required($t('[[[Wybranie modułu jest wymagane]]]')),
            sectionId: schema.string().nullable().required($t('[[[Wybranie sekcji jest wymagane]]]'))
        })
    }));
}

defineExpose({
    validate
});
</script>

<template>
    <div class="alert alert-light pb-1">
        <template v-if="form.settings.mode == 'module'">
            <div class="d-flex flex-column flex-md-row flex-md-wrap gap-2" :class="hasModuleSettingsErrors ? 'align-items-center' : 'align-items-end'">
                <div class="flex-grow-1" style="min-width: 240px;">
                    <IdeoFormGroup :label="$t('[[[Moduł dynamiczny]]]')" :invalid-feedback="form.$errors.first('settings.moduleId')" :state="form.$errors.state('settings.moduleId')" required>
                        <IdeoFormSelect v-model="form.settings.moduleId" type="text" name="settings.moduleId" :options="modules" />
                    </IdeoFormGroup>
                </div>
                <div class="flex-grow-1" style="min-width: 240px;">
                    <IdeoFormGroup :label="$t('[[[Sekcja modułu]]]')" :invalid-feedback="form.$errors.first('settings.sectionId')" :state="form.$errors.state('settings.sectionId')" required>
                        <IdeoFormSelect v-model="form.settings.sectionId" type="text" name="settings.sectionId" :options="sections" :disabled="!form.settings.moduleId" />
                    </IdeoFormGroup>
                </div>
                <div v-if="!form.settings.moduleId && !form.settings.sectionId" class="pb-3">
                    <ModulesWizard
                        mode="module"
                        :expert="false"
                        :module="form.settings.moduleId"
                        size="small"
                        @done="onWizardDone"
                    />
                </div>
            </div>
        </template>

        <IdeoFormGroup :label="$t('[[[Powiadomienia dla użytkowników]]]')">
            <div class="d-flex flex-column flex-md-row">
                <div class="flex-grow-1">
                    <IdeoAutocomplete
                        v-model="userId"
                        name="userId"
                        track-by="id"
                        :placeholder="$t('[[[Wyszukaj użytkownika...]]]')"
                        :fetch="(id: number) => fetchUser(id)"
                        :search="(query: string, limit: number) => searchUser(query, limit)"
                    >
                        <template #selected="{ option }">
                            <div class="d-flex flex-nowrap align-items-center">
                                <IdeoImg
                                    :src="$filters.image(option.facePictureUrl, 'ico')"
                                    rounded="circle"
                                    :blank="!option.facePictureUrl"
                                    width="18"
                                    height="18"
                                    blank-color="#777"
                                />
                                <b class="ms-2">{{ option.givenName }} {{ option.surname }}</b>
                                <i class="ms-auto">{{ option.email }}</i>
                            </div>
                        </template>
                        <template #option="{ option }">
                            <div class="d-flex flex-nowrap align-items-center">
                                <IdeoImg
                                    :src="$filters.image(option.facePictureUrl, 'ico')"
                                    rounded="circle"
                                    :blank="!option.facePictureUrl"
                                    width="36"
                                    height="36"
                                    blank-color="#777"
                                />
                                <b class="ms-2">{{ option.givenName }} {{ option.surname }}</b>
                                <i class="ms-auto">{{ option.email }}</i>
                            </div>
                        </template>
                    </IdeoAutocomplete>
                </div>
                <div class="d-flex align-items-stretch">
                    <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addUser()">
                        <i class="fas fa-plus" />
                    </button>
                </div>
            </div>
        </IdeoFormGroup>

        <ListView :items="users" :preload="false">
            <template #row="{ item }">
                <ListViewItem :title="$t('[[[Użytkownik]]]')">
                    {{ item.givenName }} {{ item.surname }}
                </ListViewItem>
                <ListViewItem :title="$t('[[[E-mail]]]')">
                    {{ item.email }}
                </ListViewItem>
                <ListViewButtons>
                    <ListViewButton type="button" @click="deleteUser(item.id)">
                        <i class="fas fa-times me-2" /> {{ $t('[[[Usuń]]]') }}
                    </ListViewButton>
                </ListViewButtons>
            </template>
        </ListView>
    </div>
</template>
